import { showNotification } from '@mantine/notifications';
import type { FeedbackVariables } from '@repo/api-codegen';
import { useFeedback } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { useCallback } from 'react';
import { AIFeedbackModalContent } from '../../../components/AIAssistant/AIFeedbackModal/AIFeedbackModalContent';
import type { AIFeedbackValues } from '../../../components/AIAssistant/AIFeedbackModal/constants';
import { SentimentType } from '../../../components/AIAssistant/AIFeedbackModal/constants';
import { closeAllModals, openModal } from '../../../components/ModalManager';
import queryClient from '../../queryClient';
import { useAuthUser } from '../authUser';
import { useWorkspace } from '../workspace';
import { aiEmbeddedQueryKeyFactory } from './constants';

function getTrackEventProperties(
	promptId: string,
	feedback: 'helpful' | 'unhelpful',
	values: AIFeedbackValues
): Record<string, string> {
	return {
		promptId,
		feedback,
		answerOptions: values.answerOptions.join(', '),
		details: values.details,
	};
}

export function useAIFeedback() {
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();

	const { mutateAsync: postFeedback } = useFeedback({
		onSuccess: (_, data: FeedbackVariables) => {
			queryClient.invalidateQueries({
				queryKey: aiEmbeddedQueryKeyFactory.byId(data.pathParams.promptId),
			});
		},
	});

	const submitFeedback = useCallback(
		(
			promptId: string,
			feedback: 'helpful' | 'unhelpful',
			values: AIFeedbackValues
		) => {
			trackEvent(
				`ai/feedback/${feedback === 'helpful' ? 'like' : 'dislike'}`,
				getTrackEventProperties(promptId, feedback, values),
				user,
				workspace
			);
			postFeedback({
				pathParams: {
					promptId,
				},
				body: {
					sentiment: values.sentiment,
					answer_options: values.answerOptions.join(', '),
					details: values.details,
				},
			});
			closeAllModals();
			showNotification({
				title: 'Success',
				message: 'Your feedback has been recorded.',
				color: 'green',
			});
		},
		[postFeedback, user, workspace]
	);

	const showPositiveFeedbackModal = useCallback(
		(promptId: string, onSubmit?: (sentiment: SentimentType) => void) => {
			openModal({
				title: 'Share your feedback',
				children: (
					<AIFeedbackModalContent
						sentiment={SentimentType.Positive}
						onCancel={() => closeAllModals()}
						onSubmit={(values: AIFeedbackValues) => {
							submitFeedback(promptId, 'helpful', values);
							onSubmit?.(SentimentType.Positive);
						}}
					/>
				),
			});
		},
		[submitFeedback]
	);

	const showNegativeFeedbackModal = useCallback(
		(promptId: string, onSubmit?: (sentiment: SentimentType) => void) => {
			openModal({
				title: 'Share your feedback',
				children: (
					<AIFeedbackModalContent
						sentiment={SentimentType.Negative}
						onCancel={() => closeAllModals()}
						onSubmit={(values: AIFeedbackValues) => {
							submitFeedback(promptId, 'unhelpful', values);
							onSubmit?.(SentimentType.Negative);
						}}
						isAnswerOptionsRequired
					/>
				),
			});
		},
		[submitFeedback]
	);

	return {
		showPositiveFeedbackModal,
		showNegativeFeedbackModal,
	};
}
